import { MagnifyImage } from "@/components/MagnifyImage";
import { PdfViewer } from "@/components/Pdf";
import {
  BodyText,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui";
import type {
  AmazonMessage,
  ChatMessage,
  ChatUserMap,
} from "@/features/Chat/types/chatMessageTypes";
import { useAmazonAttachment } from "@/hooks/useAmazonAttachment";
import { cn } from "@/lib/utils";
import { useDocument } from "@/network/useDocument";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { FileText, Image } from "lucide-react";
import { useEffect, useState } from "react";

interface PhotoMessageProps {
  message: ChatMessage | AmazonMessage;
  userMap?: ChatUserMap;
  patientDisplayName?: string; // fallback for Amazon patients
}

function isAmazonMessage(
  message: ChatMessage | AmazonMessage,
): message is AmazonMessage {
  return "amazonId" in message;
}

function getMessageType(message: ChatMessage | AmazonMessage): "pdf" | "image" {
  if (isAmazonMessage(message)) {
    return message.originalUrl?.includes(".pdf") ? "pdf" : "image";
  }
  return message.contentType === "application/pdf" ? "pdf" : "image";
}

function getMessageIcon(message: ChatMessage | AmazonMessage) {
  return getMessageType(message) === "pdf" ? (
    <FileText className="w-4 h-4" />
  ) : (
    <Image className="w-4 h-4" />
  );
}

function getMessageText(
  message: ChatMessage | AmazonMessage,
  userName: string | undefined,
) {
  const type = getMessageType(message);
  return `${userName || "An unknown user"} uploaded ${type === "pdf" ? "a document" : "an image"}.`;
}

export const PhotoMessage = ({
  message,
  userMap,
  patientDisplayName,
}: PhotoMessageProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });

  const user = message.userId ? userMap?.[message.userId] : undefined;
  const isAsync = message.modelType === "AsyncMessage";

  // Handle regular document messages
  const {
    data,
    isLoading: isDocumentLoading,
    isError: isDocumentError,
    refetch,
  } = useDocument({
    patientId: patientId as string,
    resourceId: message?.documentReferenceId,
    resourceType: "DocumentReference",
    path: "content[0].attachment.url",
    enabled: Boolean(message?.documentReferenceId),
  });

  // Handle Amazon messages
  const {
    data: amazonData,
    isLoading: isAmazonLoading,
    isError: isAmazonError,
  } = useAmazonAttachment({
    patientId: patientId as string,
    filename: isAmazonMessage(message) ? message.originalUrl || "" : "",
    encounterId: String(encounterId) || "",
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [magnifierEnabled, setMagnifierEnabled] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (data || amazonData) {
      const objectUrl = URL.createObjectURL(
        new Blob([data || amazonData], {
          type: "application/pdf",
        }),
      );
      setSrc(objectUrl);
      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }
    setSrc(null);
  }, [data, amazonData]);

  const renderImage = () => {
    const className =
      "bg-secondary rounded-lg object-contain aspect-square max-w-full max-h-full";
    if (!src) return null;
    if (magnifierEnabled) {
      return <MagnifyImage src={src} className={className} />;
    }
    return <img src={src} alt="chat preview" className={className} />;
  };

  const isLoading = isAmazonMessage(message)
    ? isAmazonLoading
    : isDocumentLoading;
  const isError = isAmazonMessage(message) ? isAmazonError : isDocumentError;
  const messageType = getMessageType(message);

  return (
    <Card
      className={cn("border w-full border-info", isAsync && "border-secondary")}
      data-testid="photo-card"
    >
      <div className="flex flex-row p-3">
        <div className="flex flex-1 gap-1 items-center">
          {getMessageIcon(message)}
          <BodyText size="sm" textColor="secondary" className="pt-0.5">
            {getMessageText(message, user?.name || patientDisplayName)}
          </BodyText>
        </div>
        {isAmazonMessage(message) ? (
          <Button
            variant="primary"
            size="xs"
            onClick={isError ? () => refetch() : () => setDialogOpen(true)}
            disabled={isLoading}
          >
            <BodyText
              size="xs"
              textColor="white"
              bold
              className="cursor-pointer"
            >
              {isLoading ? "Loading..." : isError ? "Retry Loading" : "Preview"}
            </BodyText>
          </Button>
        ) : (
          <Button
            variant="primary"
            size="xs"
            onClick={
              isError
                ? () => refetch()
                : () =>
                    navigate({
                      to: "/patient/$patientId/files/$fileId",
                      params: {
                        patientId,
                        fileId: message.documentReferenceId || "",
                      },
                    })
            }
            disabled={isLoading}
          >
            <BodyText
              size="xs"
              textColor="white"
              bold
              className="cursor-pointer"
            >
              {isLoading ? "Loading..." : isError ? "Retry Loading" : "View"}
            </BodyText>
          </Button>
        )}
      </div>
      <Dialog open={Boolean(dialogOpen && src)} onOpenChange={setDialogOpen}>
        <DialogPortal>
          <DialogOverlay />
          <DialogContent className="w-5/6 h-5/6 max-w-6xl">
            <DialogHeader>
              <div className="flex flex-row gap-2 items-center">
                <DialogTitle>Preview</DialogTitle>
                <DialogDescription className="hidden">
                  chat photo or document preview modal
                </DialogDescription>
                {messageType === "image" && (
                  <Button
                    variant="primary"
                    size="xs"
                    onClick={() => setMagnifierEnabled((prev) => !prev)}
                  >
                    <BodyText size="sm" textColor="white">
                      {magnifierEnabled ? "Disable" : "Enable"} Magnifier
                    </BodyText>
                  </Button>
                )}
              </div>
            </DialogHeader>
            <div className="flex flex-col max-w-full max-h-full overflow-hidden flex-1">
              {messageType === "pdf" ? (
                <PdfViewer
                  src={src}
                  showZoomControls={true}
                  containerClassName="h-full"
                />
              ) : (
                renderImage()
              )}
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </Card>
  );
};
